import { useCallback, useEffect, useState } from 'react';
import { ZodIssueCode, z } from 'zod';
import {
	SfFigure2AndChildHoldinghandsIcon,
	SfFigureAndChildHoldinghandsIcon,
	SfFigureStandIcon,
	SfFigureWalkIcon,
} from '@ninja/icons/web';
import { Path, RadioCardOption, ReactForm, useReactForm } from '@ninja/utilities';
import {
	CreateJourneyError,
	GlobalAPIError,
	postCreateJourney,
	postUpdateJourney,
} from '@shory-health-individual/customer-api';
import { ChannelType, ErrorCode, Gender, PolicyType, Relationship, SalaryBands } from '../../../../common';
import { useAppPlatformContextOptions } from '../../../../contexts/app';
import { JourneyDetails } from '../../../../server';
import {
	selectIsRedirectFormIcp,
	selectSetIsOfflineSubmitted,
	selectSetIsRedirectFormIcp,
	selectSetJourneyId,
	useQuoteStore,
} from '../../../../stores';
import { usePolicyTypes } from '../../../app-init-data';
import { usePolicyTypeFieldsStructure } from '../../members';
import {
	useCommonFieldsStructure,
	useDependentsFieldsStructure,
	useDomesticWorkerFieldsStructure,
	usePrincipalFieldsStructure,
} from './fields-structures';
import { aggregateValidations } from './helpers/aggregate-validations';
import { GetDependentValidationError, IMembersForm } from './types';

type UseMembersFormProps = {
	onSuccess: ({ journeyId }: { journeyId: string }) => void;
	journey?: JourneyDetails;
	handleErrorScroll: () => void;
	maximumNoOfMember: number;
	isWebView: boolean;
};

const isInvalidEnumValue = (form: ReactForm<IMembersForm>, field: Path<IMembersForm>) =>
	form.getFieldState(field).error?.type === z.ZodIssueCode.invalid_enum_value;

const getPlanCategoryId = (formValues: IMembersForm) => {
	const salaryBandId =
		formValues.policyTypeId === PolicyType.DomesticWorker
			? formValues.domesticWorker?.salaryBandId
			: formValues.principalDetails?.salaryBandId;

	return salaryBandId === SalaryBands.BelowOrUpTo4000 ? formValues.planCategoryId : undefined;
};

const getGender = (relationshipTypeId: Relationship, principalGenderId?: Gender): Gender => {
	switch (relationshipTypeId) {
		case Relationship.Mother:
		case Relationship.Daughter:
			return Gender.Female;
		case Relationship.Father:
		case Relationship.Son:
			return Gender.Male;
		case Relationship.Spouse:
			return principalGenderId === Gender.Male ? Gender.Female : Gender.Male;
		default:
			throw new Error(`Unsupported relationship: ${relationshipTypeId}`);
	}
};

const getInitialValues = (
	members: JourneyDetails | undefined,
	policyTypes: {
		type: string;
		id: number;
	}[]
): IMembersForm => ({
	policyTypeId: policyTypes.length === 1 ? policyTypes[0].id : members?.policyType,
	planCategoryId: members?.quotePlanCategoryId,
	emirateId: members?.emirateId,
	principalDetails: members?.principalDetails ?? {},
	dependents:
		members?.policyType === PolicyType.MeAndFamily || members?.policyType === PolicyType.OnlyFamily
			? [...(members.dependents ?? [{}])]
			: [{}],
	domesticWorker: members?.policyType === PolicyType.DomesticWorker ? members.dependents?.[0] ?? {} : {},
});

const getDependents = (formValues: IMembersForm) => {
	switch (formValues.policyTypeId) {
		case PolicyType.MeAndFamily:
		case PolicyType.OnlyFamily:
			return formValues.dependents?.map((d) => ({
				relationshipTypeId: d.relationshipTypeId!,
				dateOfBirth: d.dateOfBirth!,
				nationalityId: d.nationalityId!,
				genderId: getGender(d.relationshipTypeId!, formValues.principalDetails.genderId),
				memberId: d.memberId!,
				maritalStatusId: d.maritalStatusId!,
			}));
		case PolicyType.DomesticWorker:
			return [
				{
					genderId: formValues.domesticWorker!.genderId!,
					nationalityId: formValues.domesticWorker!.nationalityId!,
					dateOfBirth: formValues.domesticWorker!.dateOfBirth!,
					salaryBandId: formValues.domesticWorker!.salaryBandId!,
					relationshipTypeId: Relationship.DomesticWorker,
					memberId: formValues.domesticWorker!.memberId!,
					maritalStatusId: formValues.domesticWorker!.maritalStatusId!,
				},
			];
		default:
			return undefined;
	}
};

export const useMembersForm = ({
	onSuccess,
	journey,
	handleErrorScroll,
	maximumNoOfMember,
	isWebView,
}: UseMembersFormProps) => {
	const { apiConnectionOptions, onGeneralErrorHandling } = useAppPlatformContextOptions();
	const commonFields = useCommonFieldsStructure();
	const principalFields = usePrincipalFieldsStructure();
	const domesticWorkerFields = useDomesticWorkerFieldsStructure();
	const dependentsFields = useDependentsFieldsStructure();
	const setIsRedirectFormIcp = useQuoteStore(selectSetIsRedirectFormIcp);
	const { policyTypes } = usePolicyTypes();
	const setJourneyId = useQuoteStore(selectSetJourneyId);
	const setIsOfflineSubmitted = useQuoteStore(selectSetIsOfflineSubmitted);
	const [errorCode, setErrorCode] = useState<ErrorCode | undefined>(undefined);
	const [openItems, setOpenItems] = useState<React.Key[]>([0]);
	const policyTypesIcons = {
		[PolicyType.OnlyMe]: SfFigureStandIcon,
		[PolicyType.MeAndFamily]: SfFigure2AndChildHoldinghandsIcon,
		[PolicyType.OnlyFamily]: SfFigureAndChildHoldinghandsIcon,
		[PolicyType.DomesticWorker]: SfFigureWalkIcon,
	};
	const isRedirectFromIcp = useQuoteStore(selectIsRedirectFormIcp);
	const getPolicyTypeOptions = (
		policyTypes: {
			id: PolicyType;
			type: string;
		}[]
	): RadioCardOption<number>[] =>
		policyTypes.map((p) => ({
			id: p.id,
			label: p.type,
			iconStart: policyTypesIcons?.[p.id],
		}));
	const policyTypeOptions = getPolicyTypeOptions(policyTypes);
	const policyTypeFieldsStructure = usePolicyTypeFieldsStructure<IMembersForm>({ policyTypeOptions });

	const getValidations = (data: IMembersForm) =>
		aggregateValidations(
			data,
			commonFields.getValidations,
			principalFields.getValidations,
			dependentsFields.getValidations,
			domesticWorkerFields.getValidations,
			maximumNoOfMember
		);

	const getChannelType = () => {
		if (isRedirectFromIcp) return ChannelType.Icp;
		else if (isWebView) return ChannelType.MobileApp;
		else return ChannelType.Website;
	};

	const form = useReactForm<IMembersForm>({
		initialValues: getInitialValues(journey, policyTypes),
		validationMode: 'zod',
		validation: getValidations,
		validateBehavior: 'on-submit',
		onFormSubmit: async (formValues) => {
			const membersData = {
				policyTypeId: formValues.policyTypeId!,
				planCategoryId: getPlanCategoryId(formValues),
				emirateId: formValues.emirateId!,
				channelTypeId: getChannelType(),
				principalDetails: {
					maritalStatusId: formValues.principalDetails.maritalStatusId!,
					genderId: formValues.principalDetails.genderId!,
					dateOfBirth: formValues.principalDetails.dateOfBirth!,
					nationalityId: formValues.principalDetails.nationalityId!,
					memberId: formValues.principalDetails.memberId ?? '',
					salaryBandId:
						formValues.policyTypeId !== PolicyType.DomesticWorker
							? formValues.principalDetails.salaryBandId
							: undefined,
				},
				dependents: getDependents(formValues),
			};

			try {
				let response = null;

				if (journey?.encryptedJourneyId) {
					const updatedData = {
						...membersData,
						channelTypeId: journey?.channelTypeId,
						encryptedJourneyId: journey?.encryptedJourneyId,
					};

					response = await postUpdateJourney(apiConnectionOptions)(updatedData);
				} else {
					response = await postCreateJourney(apiConnectionOptions)(membersData);
					setJourneyId(response.encryptedJourneyId);
					setIsRedirectFormIcp(false);
				}
				setIsOfflineSubmitted(false);

				await onSuccess({ journeyId: response.encryptedJourneyId });
			} catch (error) {
				const errorCode = (error as CreateJourneyError).mapped?.some((e) => e.code === ErrorCode.MaxNumberOfMembers);

				if (errorCode) {
					setErrorCode(ErrorCode.MaxNumberOfMembers);
				} else {
					await onGeneralErrorHandling(error as GlobalAPIError);
				}
			}
		},
	});

	const dependents = JSON.stringify(form.getValues('dependents'));
	const isDependentsHasError = form.getFieldState('dependents').invalid;
	const dependentFieldError = form.fieldsErrors.dependents;

	const isMaxMemberCountReachedError = dependentFieldError?.type === ZodIssueCode.too_big;

	const policyTypeId = form.getValues('policyTypeId');

	const getDependentValidation: GetDependentValidationError = async () => {
		const formFieldError = await form.validateField('dependents');

		if (!Array.isArray(formFieldError) && formFieldError && formFieldError.type === ZodIssueCode.too_big) {
			setErrorCode(ErrorCode.MaxNumberOfMembers);
			handleErrorScroll();

			return 'too_big';
		}

		if (formFieldError) {
			return 'form_error';
		}
	};

	useEffect(() => {
		getDependentValidation();
	}, [isMaxMemberCountReachedError, dependents]);

	useEffect(() => {
		setErrorCode(undefined);
		getDependentValidation();
	}, [policyTypeId]);

	useEffect(() => {
		if (isDependentsHasError) form.validateField('dependents');
	}, [dependents, isDependentsHasError]);

	const onSubmit = useCallback(async (): Promise<void> => {
		setErrorCode(undefined);
		await form.submitForm();
		const journeyDetails = form.getValues();

		switch (journeyDetails.policyTypeId) {
			case PolicyType.OnlyMe:
				if (isInvalidEnumValue(form, 'principalDetails.nationalityId')) {
					setErrorCode(ErrorCode.InvalidNationalityForMe);
				}
				break;
			case PolicyType.MeAndFamily:
				if (
					isInvalidEnumValue(form, 'principalDetails.nationalityId') ||
					journeyDetails.dependents?.some((_, index) => isInvalidEnumValue(form, `dependents.${index}.nationalityId`))
				) {
					setErrorCode(ErrorCode.InvalidNationalityForMe);
				}
				break;
			case PolicyType.OnlyFamily:
				if (
					journeyDetails.dependents?.some((_, index) => isInvalidEnumValue(form, `dependents.${index}.nationalityId`))
				) {
					setErrorCode(ErrorCode.InvalidNationalityForDependents);
				}
				break;
			case PolicyType.DomesticWorker:
				if (isInvalidEnumValue(form, 'domesticWorker.nationalityId')) {
					setErrorCode(ErrorCode.InvalidNationalityForDependents);
				}
				break;
		}
	}, []);

	return {
		form,
		onSubmit,
		openItems,
		errorCode,
		setOpenItems,
		getDependentValidation,
		policyTypeFieldsStructure,
		isMaxMemberCountReachedError,
	};
};
